import Button from "@material-ui/core/Button";
import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import AssignProductDialog from "@saleor/components/AssignProductDialog";
import NotFoundPage from "@saleor/components/NotFoundPage";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA, PAGINATE_BY } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { commonMessages } from "@saleor/intl";
import { SearchProducts_search_edges_node } from "@saleor/searches/types/SearchProducts";
import useProductQuery from "@saleor/searches/useProductQuery";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createMetadataUpdateHandler from "@saleor/utils/handlers/metadataUpdateHandler";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { getMutationState, maybe } from "../../misc";
import { productUrl } from "../../products/urls";
import {
  CollectionInput,
  CoverageTypeEnum,
  SmopStatusEnum
} from "../../types/globalTypes";
import CollectionDetailsPage, {
  CollectionDetailsPageFormData
} from "../components/CollectionDetailsPage/CollectionDetailsPage";
import {
  useCollectionAssignProductMutation,
  useCollectionCreateDeeplink,
  useCollectionRemoveMutation,
  useCollectionUpdateMutation,
  useUnassignCollectionProductMutation
} from "../mutations";
import {
  TypedCollectionDetailsQuery,
  useBrandQuery,
  useStoreListQuery
} from "../queries";
import { CollectionUpdate } from "../types/CollectionUpdate";
import {
  collectionListUrl,
  collectionUrl,
  CollectionUrlDialog,
  CollectionUrlQueryParams
} from "../urls";

interface CollectionDetailsProps {
  id: string;
  params: CollectionUrlQueryParams;
}

export const CollectionDetails: React.FC<CollectionDetailsProps> = ({
  id,
  params
}) => {
  const [currentBrand, setCurrentBrand] = React.useState<string | null>(null);
  const [currentMall, setCurrentMall] = React.useState<string | null>(null);
  const [filterPublishedOnly, setFilterPublishedOnly] = React.useState<boolean>(
    false
  );
  const [
    refetchCollectionDetails,
    setRefetchCollectionDetails
  ] = React.useState(false);

  const navigate = useNavigator();
  const notify = useNotifier();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const paginate = usePaginator();
  const intl = useIntl();

  const [productSearch, setProductSearch] = React.useState("");
  const {
    handleProductSearch,
    handleSearchBy,
    loadMore: loadMoreProducts,
    result
  } = useProductQuery({
    ...DEFAULT_INITIAL_SEARCH_DATA,
    brands: currentBrand ? [currentBrand] : [],
    isVisible: filterPublishedOnly ? { isVisible: true } : undefined,
    malls: currentMall ? [currentMall] : []
  });

  React.useEffect(() => {
    handleProductSearch(productSearch);
  }, [productSearch]);

  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});

  const handleCollectionUpdate = (data: CollectionUpdate) => {
    if (data?.collectionUpdate?.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
      navigate(collectionUrl(id));
    } else if (
      data?.collectionUpdate?.errors.some(e => e.code === "INVALID") &&
      data?.collectionUpdate?.errors.some(e => e.field === "iconImage")
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Header icon resolution should not exceed 500x500 pixels"
        })
      });
    } else {
      notify({
        status: "error",
        text: data?.collectionUpdate?.errors[0].message
      });
    }
  };
  const [updateCollection, updateCollectionOpts] = useCollectionUpdateMutation({
    onCompleted: handleCollectionUpdate
  });

  const [assignProduct, assignProductOpts] = useCollectionAssignProductMutation(
    {
      onCompleted: data => {
        if (data?.collectionAddProducts?.errors.length === 0) {
          notify({
            status: "success",
            text: intl.formatMessage({
              defaultMessage: "Added product to collection"
            })
          });
          navigate(collectionUrl(id), true);
          setRefetchCollectionDetails(true);
        }
      }
    }
  );

  const [
    unassignProduct,
    unassignProductOpts
  ] = useUnassignCollectionProductMutation({
    onCompleted: data => {
      if (data?.collectionRemoveProducts?.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Deleted product from collection"
          })
        });
        reset();
        closeModal();
        setRefetchCollectionDetails(true);
      }
    }
  });

  const [removeCollection, removeCollectionOpts] = useCollectionRemoveMutation({
    onCompleted: data => {
      if (data?.collectionDelete?.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Deleted collection"
          })
        });
        navigate(collectionListUrl());
      }
    }
  });

  const [createDeeplink, createDeeplinkOpts] = useCollectionCreateDeeplink({
    onCompleted: data => {
      const errors = data?.collectionGenerateDynamicLink?.productErrors;
      if (errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Collection Updated",
            description: "snackbar text"
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Oops, somethin went wrong...",
            description: "snackbar text"
          })
        });
      }
    }
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    CollectionUrlDialog,
    CollectionUrlQueryParams
  >(navigate, params => collectionUrl(id, params), params);

  const paginationState = createPaginationState(PAGINATE_BY, params);
  const handleBack = () => navigate(collectionListUrl());
  const [iconToUpload, setIconToUpload] = React.useState(null);
  const [thumbnailLinkImageUpload, setThumbnailLinkImage] = React.useState(
    null
  );

  return (
    <TypedCollectionDetailsQuery
      displayLoader
      variables={{ id, ...paginationState }}
    >
      {({ data, loading, refetch }) => {
        const collection = data?.collection;
        const coverageType = data?.collection?.coverageType;

        if (coverageType === CoverageTypeEnum.NATIONWIDE) {
          setFilterPublishedOnly(true);
        }

        if (refetchCollectionDetails) {
          refetch();
          setRefetchCollectionDetails(false);
        }

        if (collection === null) {
          return <NotFoundPage onBack={handleBack} />;
        }

        const handleUpdate = async (
          formData: CollectionDetailsPageFormData
        ) => {
          const input: CollectionInput = {
            backgroundImageAlt: formData.backgroundImageAlt,
            color: formData.color,
            descriptionJson: JSON.stringify(formData.description || ""),
            featuredIn: formData.featuredIn || [],
            ...(iconToUpload && { iconImage: iconToUpload }),
            ...(formData.iconImage === null && { iconImage: iconToUpload }),
            ...(thumbnailLinkImageUpload && {
              thumbnailLinkImage: thumbnailLinkImageUpload
            }),
            ...(formData.thumbnailLinkImage === null && {
              thumbnailLinkImage: thumbnailLinkImageUpload
            }),
            isFeatured: formData.isFeatured,
            isFeaturedOnSmFulfilledPage: formData.isFeaturedOnSmFulfilledPage,
            isPublished: formData.isPublished,
            name: formData.name,
            publicationDate: formData.publicationDate,
            seo: {
              description: formData.seoDescription,
              title: formData.seoTitle
            },
            slug: formData.slug,
            isDirectPurchaseEnabled: formData.isDirectPurchaseEnabled
          };

          const result = await updateCollection({
            variables: {
              id,
              input
            }
          });
          if (
            result?.data?.collectionUpdate?.errors &&
            result?.data?.collectionUpdate?.errors[0] &&
            result?.data?.collectionUpdate?.errors[0].field === "isFeatured"
          ) {
            notify({
              status: "error",
              text: (
                <div>
                  <p style={{ margin: "-10px 0 0" }}>
                    Cannot featured the Collection.
                  </p>
                  <p style={{ margin: "0" }}>
                    Featured Collection exceeded to 3
                  </p>
                </div>
              )
            });
          }
          refetch();

          return result?.data?.collectionUpdate?.errors;
        };
        const handleSubmit = createMetadataUpdateHandler(
          data?.collection,
          handleUpdate,
          variables => updateMetadata({ variables }),
          variables => updatePrivateMetadata({ variables })
        );

        const formTransitionState = getMutationState(
          updateCollectionOpts.called,
          updateCollectionOpts.loading,
          updateCollectionOpts.data?.collectionUpdate.errors
        );

        const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
          data?.collection?.products?.pageInfo,
          paginationState,
          params
        );

        const brandInitialSearchData = {
          after: null,
          first: 20,
          query: ""
        };
        const {
          loadMore: loadMoreBrand,
          search: searchBrands,
          result: brandsQueryData
        } = useBrandQuery({
          skip: coverageType !== CoverageTypeEnum.LOCAL,
          variables: brandInitialSearchData
        });
        const sellerBrandChoices = (
          maybe(() => brandsQueryData?.data?.search?.edges, []) || []
        ).map(edge => edge.node);

        const storeInitialSearchData = {
          after: null,
          first: 20,
          isAllowedForNationwideDiscovery: true,
          query: "",
          status: SmopStatusEnum.ACT
        };
        const {
          loadMore: loadMoreStores,
          search: searchStores,
          result: storesQueryData
        } = useStoreListQuery({
          skip: coverageType !== CoverageTypeEnum.NATIONWIDE,
          variables: storeInitialSearchData
        });
        const storeChoices = (
          maybe(() => storesQueryData?.data?.search?.edges, []) || []
        ).map(edge => edge.node);

        return (
          <>
            <WindowTitle title={maybe(() => data?.collection?.name) || ""} />
            <CollectionDetailsPage
              brands={null}
              onAdd={() => {
                if (coverageType === CoverageTypeEnum.LOCAL) {
                  openModal("assign");
                } else if (coverageType === CoverageTypeEnum.NATIONWIDE) {
                  openModal("assign-by-store");
                }
              }}
              onBack={handleBack}
              disabled={loading || createDeeplinkOpts.loading}
              collection={data?.collection || null}
              errors={
                updateCollectionOpts?.data?.collectionUpdate?.errors || []
              }
              isFeatured={maybe(
                () =>
                  data?.shop?.homepageCollection?.id === data?.collection?.id,
                false
              )}
              onThumbnailLinkImageUpload={setThumbnailLinkImage}
              onIconUpload={setIconToUpload}
              onCollectionRemove={() => openModal("remove")}
              onFileSizeError={() =>
                notify({
                  status: "error",
                  text: intl.formatMessage({
                    defaultMessage:
                      "File size of the image you are trying to upload is too large."
                  }),
                  title: intl.formatMessage({
                    defaultMessage: "Can't Upload."
                  })
                })
              }
              onImageDelete={() => openModal("removeImage")}
              onImageUpload={file =>
                updateCollection({
                  variables: {
                    id,
                    input: {
                      backgroundImage: file
                    }
                  }
                })
              }
              onGenerateDeeplink={id => createDeeplink({ variables: { id } })}
              onSubmit={handleSubmit}
              onNextPage={loadNextPage}
              onPreviousPage={loadPreviousPage}
              pageInfo={pageInfo}
              onProductUnassign={(productId, event) => {
                event.stopPropagation();
                unassignProduct({
                  variables: {
                    collectionId: id,
                    productIds: [productId],
                    ...paginationState
                  }
                });
              }}
              onRowClick={id => () => navigate(productUrl(id))}
              saveButtonBarState={formTransitionState}
              toolbar={
                <Button
                  color="primary"
                  onClick={() =>
                    openModal("unassign", {
                      ids: listElements
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="Unassign"
                    description="unassign product from collection, button"
                  />
                </Button>
              }
              isChecked={isSelected}
              selected={listElements.length}
              toggle={toggle}
              toggleAll={toggleAll}
            />

            <AssignProductDialog
              open={params.action === "assign"}
              loading={result.loading}
              onClose={() => {
                closeModal();
                setProductSearch("");
                handleSearchBy("sku");
              }}
              onSubmit={products =>
                assignProduct({
                  variables: {
                    ...paginationState,
                    collectionId: id,
                    productIds: products.map(product => product.id)
                  }
                })
              }
              hasMore={result.data?.search?.pageInfo.hasNextPage || false}
              onFetchMore={loadMoreProducts}
              storesBrands={sellerBrandChoices}
              fetchStoreBrands={query => searchBrands(query)}
              fetchMoreStoreBrands={{
                hasMore:
                  maybe(
                    () => brandsQueryData?.data?.search?.pageInfo.hasNextPage
                  ) || false,
                loading: brandsQueryData.loading,
                onFetchMore: loadMoreStores
              }}
              confirmButtonState={assignProductOpts.status}
              products={
                maybe(() =>
                  result?.data?.search?.edges
                    .map(edge => edge.node)
                    .filter(suggestedProduct => suggestedProduct.id)
                ) as SearchProducts_search_edges_node[]
              }
              onSelectStoreBrand={(brand: string) => {
                setCurrentBrand(brand);
              }}
              handleSearchBy={handleSearchBy}
              productSearch={productSearch}
              handleProductSearch={event =>
                setProductSearch(event.target.value)
              }
              coverageType={coverageType}
            />
            <AssignProductDialog
              open={params.action === "assign-by-store"}
              loading={result.loading}
              onClose={() => {
                closeModal();
                setProductSearch("");
                handleSearchBy("sku");
              }}
              onSubmit={products =>
                assignProduct({
                  variables: {
                    ...paginationState,
                    collectionId: id,
                    productIds: products.map(product => product.id),
                    mallId: currentMall
                  }
                })
              }
              hasMore={result.data?.search?.pageInfo.hasNextPage || false}
              onFetchMore={loadMoreProducts}
              storesBrands={storeChoices}
              fetchStoreBrands={query => searchStores(query)}
              fetchMoreStoreBrands={{
                hasMore:
                  maybe(
                    () => storesQueryData?.data?.search?.pageInfo.hasNextPage
                  ) || false,
                loading: storesQueryData.loading,
                onFetchMore: loadMoreBrand
              }}
              confirmButtonState={assignProductOpts.status}
              products={
                maybe(() =>
                  result?.data?.search?.edges
                    .map(edge => edge.node)
                    .filter(suggestedProduct => suggestedProduct.id)
                ) as SearchProducts_search_edges_node[]
              }
              onSelectStoreBrand={(brand: string, mall?: string) => {
                setCurrentBrand(brand);
                setCurrentMall(mall || null);
              }}
              handleSearchBy={handleSearchBy}
              productSearch={productSearch}
              handleProductSearch={event =>
                setProductSearch(event.target.value)
              }
              coverageType={coverageType}
            />

            <ActionDialog
              confirmButtonState={removeCollectionOpts.status}
              onClose={closeModal}
              onConfirm={() =>
                removeCollection({
                  variables: { id }
                })
              }
              open={params.action === "remove"}
              title={intl.formatMessage({
                defaultMessage: "Delete Collection",
                description: "dialog title"
              })}
              variant="delete"
            >
              <DialogContentText>
                <FormattedMessage
                  defaultMessage="Are you sure you want to delete {collectionName}?"
                  values={{
                    collectionName: (
                      <strong>
                        {maybe(() => data?.collection?.name, "...")}
                      </strong>
                    )
                  }}
                />
              </DialogContentText>
            </ActionDialog>

            <ActionDialog
              confirmButtonState={unassignProductOpts.status}
              onClose={closeModal}
              onConfirm={() =>
                unassignProduct({
                  variables: {
                    ...paginationState,
                    collectionId: id,
                    productIds: params.ids || []
                  }
                })
              }
              open={params.action === "unassign"}
              title={intl.formatMessage({
                defaultMessage: "Unassign products from collection",
                description: "dialog title"
              })}
            >
              <DialogContentText>
                <FormattedMessage
                  defaultMessage="{counter,plural,one{Are you sure you want to unassign this product?} other{Are you sure you want to unassign {displayQuantity} products?}}"
                  values={{
                    counter: maybe(() => params?.ids?.length),
                    displayQuantity: (
                      <strong>{maybe(() => params?.ids?.length)}</strong>
                    )
                  }}
                />
              </DialogContentText>
            </ActionDialog>

            <ActionDialog
              confirmButtonState={updateCollectionOpts.status}
              onClose={closeModal}
              onConfirm={() =>
                updateCollection({
                  variables: {
                    id,
                    input: {
                      backgroundImage: null
                    }
                  }
                })
              }
              open={params.action === "removeImage"}
              title={intl.formatMessage({
                defaultMessage: "Delete image",
                description: "dialog title"
              })}
              variant="delete"
            >
              <DialogContentText>
                <FormattedMessage defaultMessage="Are you sure you want to delete collection's image?" />
              </DialogContentText>
            </ActionDialog>
          </>
        );
      }}
    </TypedCollectionDetailsQuery>
  );
};
export default CollectionDetails;
